import { PdBaseUI } from "../base/pd-base-ui.js";
import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { calculateFloatingPosition } from "@progressive-development/pd-utils";
//#region src/pd-hover-box/PdHoverBox.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
var hoverBoxIdCounter = 0;
/**
* Floating info box that appears on click.
*
* The tooltip is rendered as a portal in `document.body` to escape
* CSS containing blocks created by `transform`, `will-change`, etc.
*
* @tagname pd-hover-box
* @summary Click-to-show info tooltip with auto-positioning.
*
* @slot normal-view - Trigger element (e.g., icon).
* @slot info-view - Info content to display.
*
* @cssprop --pd-hover-box-bg-col - Info box background. Default: `var(--pd-default-dark-col)`.
* @cssprop --pd-hover-box-border-col - Info box border color. Default: `var(--pd-default-col)`.
* @cssprop --pd-hover-box-width - Info box width. Default: `240px`.
* @cssprop --pd-hover-box-font-col - Info box text color. Default: `var(--pd-default-bg-col)`.
* @cssprop --pd-hover-box-font-size - Info box font size. Default: `0.8em`.
*/
var PdHoverBox = class extends PdBaseUI {
	constructor(..._args) {
		super(..._args);
		this.triggerLabel = "Show info";
		this._visible = false;
		this._infoId = `pd-hover-box-${++hoverBoxIdCounter}`;
		this._portalEl = null;
		this._boundHandleClickOutside = this._handleClickOutside.bind(this);
		this._boundHandleKeyDown = this._handleKeyDown.bind(this);
		this._boundHandleScroll = this._handleScroll.bind(this);
	}
	static {
		this.styles = [PdBaseUI.styles, css`
      :host {
        display: block;
      }

      .hover-box {
        position: relative;
      }

      /* Trigger styles */
      .trigger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }

      .trigger:focus {
        outline: none;
      }

      .trigger:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col);
        outline-offset: 2px;
        border-radius: var(--pd-radius-sm, 2px);
      }

      /* Slot kept for content projection — hidden, read by portal */
      .info-source {
        display: none;
      }
    `];
	}
	render() {
		return html`
      <div class="hover-box">
        <div
          class="trigger"
          tabindex="0"
          role="button"
          @click="${this._toggleInfo}"
          @keydown="${this._onTriggerKeyDown}"
          aria-expanded="${this._visible}"
          aria-controls="${this._infoId}"
          aria-label="${this.triggerLabel || nothing}"
        >
          <slot name="normal-view"></slot>
        </div>
        <div class="info-source">
          <slot name="info-view"></slot>
        </div>
      </div>
    `;
	}
	disconnectedCallback() {
		this._closeInfo();
		this._removePortal();
		super.disconnectedCallback();
	}
	/** Creates the portal element in document.body (lazy, once). */
	_ensurePortal() {
		if (!this._portalEl) {
			const el = document.createElement("div");
			el.id = this._infoId;
			el.setAttribute("role", "tooltip");
			el.setAttribute("aria-hidden", "true");
			el.style.cssText = `
        position: fixed;
        z-index: 9999;
        text-align: center;
        box-sizing: border-box;
        pointer-events: none;
        visibility: hidden;
        opacity: 0;
        transition: opacity 0.2s ease-in;
      `;
			document.body.appendChild(el);
			this._portalEl = el;
		}
		return this._portalEl;
	}
	/** Removes the portal element from document.body. */
	_removePortal() {
		this._portalEl?.remove();
		this._portalEl = null;
	}
	/** Clones the info-view slot content into the portal. */
	_syncPortalContent() {
		const portal = this._ensurePortal();
		const slot = this.shadowRoot?.querySelector("slot[name=\"info-view\"]");
		if (!slot) return;
		portal.innerHTML = "";
		for (const node of slot.assignedNodes({ flatten: true })) portal.appendChild(node.cloneNode(true));
	}
	/**
	* Reads CSS custom properties from the component context and applies
	* them as concrete styles on the portal (which lives outside the
	* component's cascade).
	*/
	_applyPortalStyles() {
		const portal = this._portalEl;
		if (!portal) return;
		const cs = getComputedStyle(this);
		const read = (prop) => cs.getPropertyValue(prop).trim();
		const bgCol = read("--pd-hover-box-bg-col") || read("--pd-default-dark-col") || "#0a3a48";
		const borderCol = read("--pd-hover-box-border-col") || read("--pd-default-col") || "#067394";
		const fontCol = read("--pd-hover-box-font-col") || read("--pd-default-bg-col") || "#fff";
		const fontSize = read("--pd-hover-box-font-size") || "0.8em";
		const padding = read("--pd-spacing-sm") || "0.5rem";
		const width = read("--pd-hover-box-width") || (window.innerWidth <= 640 ? "190px" : "240px");
		portal.style.backgroundColor = bgCol;
		portal.style.border = `1px solid ${borderCol}`;
		portal.style.color = fontCol;
		portal.style.fontSize = fontSize;
		portal.style.padding = padding;
		portal.style.width = width;
	}
	/**
	* Calculates fixed position based on trigger location and available space.
	* Uses viewport-relative coordinates for position: fixed.
	*/
	_calculatePosition() {
		const triggerEl = this.shadowRoot?.querySelector(".trigger");
		const portal = this._portalEl;
		if (!triggerEl || !portal) return;
		const pos = calculateFloatingPosition({
			anchor: triggerEl.getBoundingClientRect(),
			floating: {
				width: portal.offsetWidth || 240,
				height: portal.offsetHeight || 100
			}
		});
		portal.style.top = `${pos.top}px`;
		portal.style.left = `${pos.left}px`;
	}
	/**
	* Opens the info box and registers close listeners.
	*/
	_openInfo() {
		this._ensurePortal();
		this._syncPortalContent();
		this._applyPortalStyles();
		this._calculatePosition();
		const portal = this._portalEl;
		portal.style.visibility = "visible";
		portal.style.opacity = "1";
		portal.setAttribute("aria-hidden", "false");
		this._visible = true;
		document.addEventListener("click", this._boundHandleClickOutside);
		document.addEventListener("keydown", this._boundHandleKeyDown);
		window.addEventListener("scroll", this._boundHandleScroll, true);
	}
	/**
	* Closes the info box and removes close listeners.
	*/
	_closeInfo() {
		if (this._portalEl) {
			this._portalEl.style.visibility = "hidden";
			this._portalEl.style.opacity = "0";
			this._portalEl.setAttribute("aria-hidden", "true");
		}
		this._visible = false;
		document.removeEventListener("click", this._boundHandleClickOutside);
		document.removeEventListener("keydown", this._boundHandleKeyDown);
		window.removeEventListener("scroll", this._boundHandleScroll, true);
	}
	/**
	* Toggles the info box visibility.
	*/
	_toggleInfo(e) {
		e.stopPropagation();
		if (this._visible) this._closeInfo();
		else this._openInfo();
	}
	/**
	* Handles keyboard events on the trigger.
	*/
	_onTriggerKeyDown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			this._toggleInfo(e);
		}
	}
	/**
	* Handles Escape key to close.
	*/
	_handleKeyDown(e) {
		if (e.key === "Escape") {
			this._closeInfo();
			(this.shadowRoot?.querySelector(".trigger"))?.focus();
		}
	}
	/**
	* Handles clicks outside to close.
	*/
	_handleClickOutside(e) {
		const path = e.composedPath();
		if (!path.includes(this) && (!this._portalEl || !path.includes(this._portalEl))) this._closeInfo();
	}
	/**
	* Handles scroll to close (fixed position doesn't follow scroll).
	*/
	_handleScroll() {
		this._closeInfo();
	}
};
__decorate([property({
	type: String,
	attribute: "trigger-label"
})], PdHoverBox.prototype, "triggerLabel", void 0);
__decorate([state()], PdHoverBox.prototype, "_visible", void 0);
//#endregion
export { PdHoverBox };
